import React, { useState, useEffect, useRef } from 'react';
import {
  TextField, Box, Typography, Card, CardMedia, Grid, Pagination,
  InputAdornment, IconButton, CircularProgress, Button, Chip,
  Select, MenuItem, SelectChangeEvent, FormControlLabel, Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SortIcon from '@mui/icons-material/Sort';
import BaseDialog from './BaseDialog';
import { searchAnimeSelect, AnimeSelectResult } from '../../services/animeApi';

export { type AnimeSelectResult };

interface AnimeSelectDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect?: (anime: AnimeSelectResult) => void;
  onMultiSelect?: (anime: AnimeSelectResult[]) => void;
  title?: string;
  excludeMalIds?: number[];
  showAllSearchOption?: boolean;
  multiSelect?: boolean;
  maxSelect?: number;
}

const statusOptions = [
  { value: 1, label: '視聴済み' },
  { value: 2, label: '視聴中' },
  { value: 3, label: '中断' },
  { value: 4, label: '視聴中止' },
  { value: 5, label: 'ウォッチリスト' },
];

const defaultStatuses = [1, 2, 3];

const orderOptions = [
  { value: 'default', label: 'デフォルト' },
  { value: 'rating_desc', label: '評価 (高い順)' },
  { value: 'rating', label: '評価 (低い順)' },
  { value: 'start_date_desc', label: '視聴開始日 (新しい順)' },
  { value: 'start_date', label: '視聴開始日 (古い順)' },
  { value: 'added_date_desc', label: '追加日 (新しい順)' },
  { value: 'added_date', label: '追加日 (古い順)' },
];

const getCoverUrl = (imageUrl: string | null | undefined) => {
  if (!imageUrl) return '/image/no_image.png';
  return imageUrl;
};

const AnimeSelectDialog: React.FC<AnimeSelectDialogProps> = ({
  open,
  onClose,
  onSelect,
  onMultiSelect,
  title = 'アニメを選択',
  excludeMalIds = [],
  showAllSearchOption = false,
  multiSelect = false,
  maxSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<AnimeSelectResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [inLibrary, setInLibrary] = useState(true);
  const [selectedItems, setSelectedItems] = useState<AnimeSelectResult[]>([]);
  const [order, setOrder] = useState('default');
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>(defaultStatuses);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, []);

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setPage(1);
      setInLibrary(true);
      setSelectedItems([]);
      setOrder('default');
      setSelectedStatuses(defaultStatuses);
      fetchItems('', 1, true, 'default', defaultStatuses);
    }
  }, [open]);

  const fetchItems = async (
    query: string,
    pageNum: number,
    libraryOnly: boolean = inLibrary,
    sortOrder: string = order,
    statuses: number[] = selectedStatuses,
  ) => {
    setLoading(true);
    try {
      const response = await searchAnimeSelect(query, libraryOnly, pageNum, excludeMalIds, sortOrder, statuses);
      setItems(response.results);
      setTotalPages(Math.ceil(response.count / response.page_size));
    } catch (err) {
      console.error('Failed to fetch anime:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPage(1); fetchItems(searchQuery, 1); };
  const handleClearSearch = () => { setSearchQuery(''); setPage(1); fetchItems('', 1); };
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => { setPage(value); fetchItems(searchQuery, value); };

  const handleLibraryToggle = (checked: boolean) => {
    setInLibrary(checked);
    setPage(1);
    if (!checked) { setOrder('default'); fetchItems(searchQuery, 1, checked, 'default', selectedStatuses); }
    else { fetchItems(searchQuery, 1, checked, order, selectedStatuses); }
  };

  const handleStatusToggle = (statusValue: number) => {
    const newStatuses = selectedStatuses.includes(statusValue)
      ? selectedStatuses.filter(s => s !== statusValue)
      : [...selectedStatuses, statusValue].sort();
    if (newStatuses.length === 0) return;
    setSelectedStatuses(newStatuses);
    setPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { fetchItems(searchQuery, 1, inLibrary, order, newStatuses); }, 500);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setOrder(newOrder);
    setPage(1);
    fetchItems(searchQuery, 1, inLibrary, newOrder);
  };

  const handleItemClick = (item: AnimeSelectResult) => {
    if (multiSelect) {
      setSelectedItems(prev => {
        const exists = prev.some(i => i.mal_id === item.mal_id);
        if (exists) return prev.filter(i => i.mal_id !== item.mal_id);
        if (maxSelect !== undefined && prev.length >= maxSelect) return prev;
        return [...prev, item];
      });
    } else {
      onSelect?.(item);
      onClose();
    }
  };

  const handleConfirmMultiSelect = () => { onMultiSelect?.(selectedItems); onClose(); };
  const isSelected = (malId: number) => selectedItems.some(i => i.mal_id === malId);

  const actions = multiSelect ? (
    <>
      <Button onClick={onClose} size="small">キャンセル</Button>
      <Button onClick={handleConfirmMultiSelect} variant="contained" size="small" disabled={selectedItems.length === 0}>
        {selectedItems.length > 0
          ? maxSelect !== undefined ? `${selectedItems.length}/${maxSelect}件を追加` : `${selectedItems.length}件を追加`
          : '追加'}
      </Button>
    </>
  ) : undefined;

  return (
    <BaseDialog open={open} onClose={onClose} title={title} maxWidth="md" actions={actions}>
      <Box sx={{ minHeight: '85vh' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {inLibrary ? 'ライブラリからアニメを選択' : 'すべてのアニメから検索'}
          {multiSelect && maxSelect !== undefined
            ? ` (${selectedItems.length}/${maxSelect}件選択中)`
            : multiSelect && selectedItems.length > 0 ? ` (${selectedItems.length}件選択中)` : ''}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth size="small"
              placeholder={inLibrary ? 'ライブラリ内を検索...' : 'すべてのアニメを検索...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}><ClearIcon /></IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {inLibrary && (
              <Select value={order} onChange={handleOrderChange} size="small"
                sx={{
                  minWidth: { xs: 80, sm: 180 }, height: 32, fontSize: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)', borderRadius: '4px',
                  '& .MuiSelect-select': { paddingY: 0, paddingX: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                    <SortIcon sx={{ mr: 0.5, fontSize: '1rem', flexShrink: 0 }} />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {orderOptions.find(o => o.value === selected)?.label || '並び替え'}
                    </Box>
                  </Box>
                )}
              >
                {orderOptions.map(opt => <MenuItem key={opt.value} value={opt.value} dense>{opt.label}</MenuItem>)}
              </Select>
            )}
          </Box>
          {inLibrary && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {statusOptions.map(opt => (
                <Chip key={opt.value} label={opt.label} size="small"
                  variant={selectedStatuses.includes(opt.value) ? 'filled' : 'outlined'}
                  color={selectedStatuses.includes(opt.value) ? 'primary' : 'default'}
                  onClick={() => handleStatusToggle(opt.value)}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              ))}
            </Box>
          )}
          {showAllSearchOption && (
            <FormControlLabel
              control={<Switch checked={!inLibrary} onChange={(e) => handleLibraryToggle(!e.target.checked)} size="small" />}
              label={<Typography variant="caption" color="text.secondary">ライブラリ以外も含めて検索</Typography>}
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {multiSelect && selectedItems.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedItems.map(item => (
              <Chip key={item.mal_id} label={item.title_japanese || item.title} size="small"
                onDelete={() => setSelectedItems(prev => prev.filter(i => i.mal_id !== item.mal_id))} />
            ))}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {!inLibrary && searchQuery.length < 2 ? '2文字以上で検索してください' : 'アニメが見つかりませんでした'}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={1}>
              {items.map((item) => (
                <Grid item xs={4} sm={3} md={2} key={item.mal_id}>
                  <Card onClick={() => handleItemClick(item)}
                    sx={{
                      cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
                      border: isSelected(item.mal_id) ? '2px solid' : '1px solid transparent',
                      borderColor: isSelected(item.mal_id) ? 'primary.main' : 'transparent',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
                    }}
                  >
                    <CardMedia component="img" image={getCoverUrl(item.image_url)} alt={item.title_japanese || item.title}
                      sx={{ aspectRatio: '2/3', objectFit: 'cover' }} />
                    {multiSelect && isSelected(item.mal_id) && (
                      <CheckCircleIcon sx={{ position: 'absolute', top: 4, right: 4, color: 'primary.main', backgroundColor: 'background.paper', borderRadius: '50%', fontSize: 22 }} />
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
              </Box>
            )}
          </>
        )}
      </Box>
    </BaseDialog>
  );
};

export default AnimeSelectDialog;
